import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Btn } from "@/components/ui-kit";
import { History, Lock } from "lucide-react";

const when = (iso) => new Date(iso).toLocaleString();
const device = (ua = "") => (/mobile|android|iphone/i.test(ua) ? "Mobile" : "Desktop") + (/(Chrome|Firefox|Safari|Edg)/.exec(ua)?.[1] ? ` · ${/(Edg|Chrome|Firefox|Safari)/.exec(ua)[1]}` : "");

export function LoginHistory() {
  const [data, setData] = useState({ events: [], locked: [] });
  const [filter, setFilter] = useState("all");
  const load = () => { api.get("/auth/login-history").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const unlock = async (email) => {
    try { await api.post("/auth/unlock", { email }); toast.success(`${email} unlocked`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const events = data.events.filter((e) => filter === "all" || (filter === "ok" ? e.success : !e.success));

  return (
    <Card className="p-3" data-testid="login-history">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-heading text-sm font-bold uppercase tracking-wide"><History size={15} /> Login History</div>
        <div className="flex gap-1">
          {[["all", "All"], ["ok", "Sign-ins"], ["fail", "Failed"]].map(([k, l]) => (
            <Btn key={k} size="sm" variant={filter === k ? "primary" : "outline"} onClick={() => setFilter(k)} data-testid={`lh-filter-${k}`}>{l}</Btn>
          ))}
          <Btn size="sm" variant="outline" onClick={load} data-testid="lh-refresh">Refresh</Btn>
        </div>
      </div>
      {data.locked.length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid="locked-accounts">
          {data.locked.map((l) => (
            <div key={l.email} className="flex items-center gap-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm" data-testid={`locked-${l.email}`}>
              <Lock size={14} className="text-red-500" />
              <span className="flex-1 font-mono text-xs">{l.email} — locked until {when(l.locked_until)}</span>
              <Btn size="sm" onClick={() => unlock(l.email)} data-testid={`unlock-${l.email}`}>Unlock</Btn>
            </div>
          ))}
        </div>
      )}
      <div className="max-h-96 overflow-auto">
        <table className="w-full min-w-[640px] text-xs">
          <thead><tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-1.5">Time</th><th className="px-2 py-1.5">Email</th><th className="px-2 py-1.5">Result</th><th className="px-2 py-1.5">IP</th><th className="px-2 py-1.5">Device</th>
          </tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className="border-b border-border/60" data-testid={`lh-row-${e.id}`}>
                <td className="px-2 py-1.5 whitespace-nowrap">{when(e.created_at)}</td>
                <td className="px-2 py-1.5 font-mono">{e.email}</td>
                <td className={`px-2 py-1.5 font-semibold ${e.success ? "text-emerald-500" : "text-red-500"}`}>{e.reason}</td>
                <td className="px-2 py-1.5 font-mono">{e.ip}</td>
                <td className="px-2 py-1.5">{device(e.user_agent)}</td>
              </tr>
            ))}
            {!events.length && <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">No sign-in activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
